import {css} from '@emotion/react'
import ArrowRightIcon from '../../../images/vanish/icons/arrow-right-icon.svg'

const translations = {
    en: {
        title: 'How it works',
        subtitle:
            'Startpage is committed to being the most private and transparent AI provider. AI runs in Vanish while Startpage search remains unchanged, and every AI product will be optional and clearly labeled.',
        features: [
            {
                text: 'You ask Private AI a question',
                subtext: '',
            },
            {
                text: 'Your prompt is handed privately to Startpage',
                subtext: 'Startpage hides and deletes your personal details.',
            },
            {
                text: 'Startpage sends the anonymized prompt to the AI model',
                subtext: 'The AI model only sees the prompt, not who asked it.',
            },
            {
                text: 'The AI model writes a response and sends it back to Startpage',
                subtext: '',
            },
            {
                text: 'Startpage securely delivers the answer back to you',
                subtext: '',
            },
        ],
    },
    fr: {
        title: 'Comment ça marche',
        subtitle:
            'Startpage s’engage à être le fournisseur d’IA le plus privé et le plus transparent. L’IA fonctionne dans Vanish tandis que la recherche Startpage reste inchangée, et chaque produit d’IA sera optionnel et clairement étiqueté.',
        features: [
            {
                text: 'Vous posez une question à Private AI',
                subtext: '',
            },
            {
                text: 'Votre demande est transmise en privé à Startpage',
                subtext: 'Startpage masque et supprime vos informations personnelles.',
            },
            {
                text: 'Startpage envoie la demande anonymisée au modèle d’IA',
                subtext: 'Le modèle d’IA ne voit que la demande, pas qui l’a posée.',
            },
            {
                text: 'Le modèle d’IA rédige une réponse et la renvoie à Startpage',
                subtext: '',
            },
            {
                text: 'Startpage vous renvoie la réponse de façon sécurisée',
                subtext: '',
            },
        ],
    },
    de: {
        title: 'Wie es funktioniert',
        subtitle:
            'Startpage verpflichtet sich, der privateste und transparenteste KI-Anbieter zu sein. KI läuft in Vanish, während die Startpage-Suche unverändert bleibt, und jedes KI-Produkt ist optional und klar gekennzeichnet.',
        features: [
            {
                text: 'Sie stellen Private AI eine Frage',
                subtext: '',
            },
            {
                text: 'Ihr Prompt wird privat an Startpage übergeben',
                subtext: 'Startpage verbirgt und löscht Ihre persönlichen Daten.',
            },
            {
                text: 'Startpage sendet den anonymisierten Prompt an das KI-Modell',
                subtext: 'Das KI-Modell sieht nur den Prompt, nicht wer ihn gestellt hat.',
            },
            {
                text: 'Das KI-Modell schreibt eine Antwort und sendet sie an Startpage zurück',
                subtext: '',
            },
            {
                text: 'Startpage liefert die Antwort sicher an Sie zurück',
                subtext: '',
            },
        ],
    },
    nl: {
        title: 'Hoe het werkt',
        subtitle:
            'Startpage is toegewijd aan het zijn van de meest privé en transparante AI-aanbieder. AI draait in Vanish terwijl Startpage-zoeken ongewijzigd blijft, en elk AI-product is optioneel en duidelijk gelabeld.',
        features: [
            {
                text: 'U stelt Private AI een vraag',
                subtext: '',
            },
            {
                text: 'Uw prompt wordt privé aan Startpage overgedragen',
                subtext: 'Startpage verbergt en verwijdert uw persoonlijke gegevens.',
            },
            {
                text: 'Startpage stuurt de geanonimiseerde prompt naar het AI-model',
                subtext: 'Het AI-model ziet alleen de prompt, niet wie deze heeft gesteld.',
            },
            {
                text: 'Het AI-model schrijft een antwoord en stuurt het terug naar Startpage',
                subtext: '',
            },
            {
                text: 'Startpage levert het antwoord veilig aan u terug',
                subtext: '',
            },
        ],
    },
}

// eslint-disable-next-line no-unused-vars
const Section1 = ({languageCode = 'en', experimentId, os, browser}) => {
    const text = translations[languageCode] || translations.en

    return (
        <section
            className="vanish-section1"
            css={css`
                width: 100%;
                background: var(--background-teritary, #100026);
                padding: 104px 40px 88px;
                display: flex;
                flex-direction: column;
                justify-content: center;
                align-items: center;
                box-sizing: border-box;
                position: relative;
                z-index: 1;
                @media (max-width: 480px) {
                    padding: 72px 20px;
                }
            `}
        >
            <div
                className="content-wrapper"
                css={css`
                    width: 100%;
                    text-align: center;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                `}
            >
                <h2
                    css={css`
                        margin-bottom: 48px;
                        color: var(--text-primary, #f2f3fb);
                        text-align: center;
                        leading-trim: both;
                        text-edge: cap;
                        font-family: Inter;
                        font-size: 60px;
                        font-style: normal;
                        font-weight: 700;
                        line-height: normal;
                        letter-spacing: 0.5px;
                        @media (max-width: 480px) {
                            color: var(--text-primary, #ebecf7);
                            font-size: 42px;
                            line-height: 40px; /* 95.238% */
                        }
                    `}
                >
                    {text.title}
                </h2>

                <p
                    css={css`
                        color: var(--text-primary, #f2f3fb);
                        text-align: center;
                        leading-trim: both;
                        text-edge: cap;
                        font-family: Inter;
                        font-size: 16px;
                        font-style: normal;
                        font-weight: 400;
                        line-height: 24px; /* 133.333% */
                        margin: 0 0 47px 0;
                        max-width: 975px;
                        text-align: center;
                    `}
                >
                    {text.subtitle}
                </p>

                <div
                    className="features-grid"
                    css={css`
                        display: flex;
                        justify-content: center;
                        align-items: flex-start;
                        gap: 16px;
                        width: 100%;
                        @media (max-width: 1050px) {
                            flex-direction: column;
                            align-items: center;
                            gap: 20px;
                            max-width: 410px;
                        }
                        @media (max-width: 480px) {
                            max-width: none;
                        }
                    `}
                >
                    {text.features.map((feature, index) => (
                        <div
                            key={index}
                            className="feature-item"
                            css={css`
                                display: flex;
                                flex-direction: column;
                                align-items: center;
                                text-align: center;
                                padding: 32px 27px;
                                border-radius: 20px;
                                border: 2px solid var(--button, #8519b4);
                                max-width: 182px;
                                min-width: 182px;
                                position: relative;

                                @media (max-width: 1050px) {
                                    padding: 32px 20px;
                                    max-width: none;
                                    width: 100%;
                                }
                            `}
                        >
                            <ArrowRightIcon
                                css={css`
                                    position: absolute;
                                    top: 58px;
                                    right: -24px;
                                    z-index: 100;
                                    ${index === 4 && 'display: none;'}
                                    @media (max-width: 1050px) {
                                        transform: rotate(90deg);
                                        right: initial;
                                        top: initial;
                                        bottom: -24px;
                                    }
                                `}
                            />
                            <p
                                css={css`
                                    color: var(--text-primary, #f2f3fb);
                                    text-align: center;
                                    leading-trim: both;
                                    text-edge: cap;
                                    font-family: Inter;
                                    font-size: 16px;
                                    font-style: normal;
                                    font-weight: 600;
                                    line-height: 24px; /* 133.333% */
                                    letter-spacing: 0.6px;
                                    margin: 0;
                                `}
                            >
                                {feature.text}
                            </p>
                            {feature.subtext && (
                                <p
                                    css={css`
                                        margin-top: 20px;
                                        color: var(--text-primary, #f2f3fb);
                                        leading-trim: both;
                                        text-edge: cap;
                                        font-family: Inter;
                                        font-size: 14px;
                                        font-style: italic;
                                        font-weight: 400;
                                        line-height: 20px;
                                        letter-spacing: 0.6px;
                                    `}
                                >
                                    {feature.subtext}
                                </p>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        </section>
    )
}

export default Section1
